import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { toLightweightDeviation } from '../utils/toLightweightDeviation';
import Component from './components/DeviationNodeView';
import { deleteExtension, getExternalRenderer } from '../../components/utils/tiptap';
import { EXTENSION_TYPE_NAME } from './constants';
import { fetchFreshDeviations } from './puppy/fetchRefresh';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { elmoSaysThisMightBeADeviationUrl } from './helpers';
import { fetchDeviation } from './puppy/fetchDeviation';
import { Url } from '@wix/da-url';
export var MenuState = /*#__PURE__*/ function(MenuState) {
    MenuState[MenuState["HIDDEN"] = 0] = "HIDDEN";
    MenuState[MenuState["SHOW"] = 1] = "SHOW";
    return MenuState;
}({});
export const DaDeviation = Node.create({
    name: 'da-deviation',
    group: 'block unlinkable',
    inline: false,
    draggable: true,
    addStorage () {
        return {
            menuState: 0
        };
    },
    addOptions () {
        return {
            HTMLAttributes: {},
            defaultWidth: 780,
            smallMedia: false,
            canAlign: true,
            canLink: true,
            canConfigure: true,
            canResize: true
        };
    },
    addAttributes () {
        var _this_options_defaultAlignment;
        return {
            deviation: {
                default: null
            },
            alignment: {
                default: (_this_options_defaultAlignment = this.options.defaultAlignment) !== null && _this_options_defaultAlignment !== void 0 ? _this_options_defaultAlignment : 'center'
            },
            link: {
                default: null
            },
            width: {
                default: null
            }
        };
    },
    /**
   * Used to parse incoming html, usually via paste
   */ parseHTML () {
        return [
            {
                tag: `figure[data-da-type="${EXTENSION_TYPE_NAME}"]`,
                getAttrs (node) {
                    if (!node || !node.getAttribute) {
                        return false;
                    }
                    const dataType = node.getAttribute('data-da-type');
                    if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
                        return false;
                    }
                    try {
                        var _node_getAttribute, _node_getAttribute1, _node_getAttribute2, _node_getAttribute3;
                        return {
                            deviation: JSON.parse((_node_getAttribute = node.getAttribute('data-deviation')) !== null && _node_getAttribute !== void 0 ? _node_getAttribute : ''),
                            alignment: (_node_getAttribute1 = node.getAttribute('data-alignment')) !== null && _node_getAttribute1 !== void 0 ? _node_getAttribute1 : undefined,
                            link: (_node_getAttribute2 = node.getAttribute('data-link')) !== null && _node_getAttribute2 !== void 0 ? _node_getAttribute2 : undefined,
                            width: (_node_getAttribute3 = node.getAttribute('data-width')) !== null && _node_getAttribute3 !== void 0 ? _node_getAttribute3 : undefined
                        };
                    } catch (e) {}
                    return null;
                }
            },
            {
                tag: `a[data-da-type="${EXTENSION_TYPE_NAME}"]`,
                getAttrs (node) {
                    if (!node || !node.getAttribute) {
                        return false;
                    }
                    const figure = node.querySelector(`figure[data-da-type="${EXTENSION_TYPE_NAME}"]`);
                    if (!figure) {
                        return false;
                    }
                    try {
                        var _figure_getAttribute, _figure_getAttribute1, _figure_getAttribute2, _figure_getAttribute3;
                        return {
                            deviation: JSON.parse((_figure_getAttribute = figure.getAttribute('data-deviation')) !== null && _figure_getAttribute !== void 0 ? _figure_getAttribute : ''),
                            alignment: (_figure_getAttribute1 = figure.getAttribute('data-alignment')) !== null && _figure_getAttribute1 !== void 0 ? _figure_getAttribute1 : undefined,
                            link: (_figure_getAttribute2 = figure.getAttribute('data-link')) !== null && _figure_getAttribute2 !== void 0 ? _figure_getAttribute2 : undefined,
                            width: (_figure_getAttribute3 = figure.getAttribute('data-width')) !== null && _figure_getAttribute3 !== void 0 ? _figure_getAttribute3 : undefined
                        };
                    } catch (e) {}
                    return null;
                }
            }
        ];
    },
    /**
   * Used to render HTML version of the doc, this is not used for viewer and not used
   * in edit mode either.
   *
   * Will be used for public API on the server
   *
   * This seems to be called for some reason on unmount and then everything is also
   * undefined so make sure you guard things.
   */ renderHTML ({ HTMLAttributes: { deviation, width, alignment, link } }) {
        const deviationMediaService = getExternalRenderer(this.editor, 'mediaService');
        if (!deviationMediaService) {
            return [
                'div'
            ];
        }
        try {
            const img = [
                'img',
                mergeAttributes({
                    alt: deviation.title,
                    src: deviationMediaService.getLargestFullview(deviation).src,
                    width,
                    class: alignment
                })
            ];
            link = deviation.url;
            return [
                'figure',
                mergeAttributes({
                    'data-da-type': EXTENSION_TYPE_NAME,
                    'data-deviation': JSON.stringify(deviation),
                    'data-width': width,
                    'data-alignment': alignment,
                    'data-link': link
                }),
                link ? [
                    'a',
                    mergeAttributes({
                        href: link
                    }),
                    img
                ] : img
            ];
        } catch (e) {
            return [
                'span'
            ];
        }
    },
    addNodeView () {
        return ReactNodeViewRenderer(Component);
    },
    onSelectionUpdate () {
        if (this.editor.isActive('da-deviation')) {
            this.editor.commands.daDeviationOpenSettingsMenu();
        } else {
            this.editor.commands.daDeviationCloseSettingsMenu();
        }
    },
    addCommands () {
        return {
            daDeviationAddDeviation: (deviation)=>({ commands, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 1;
                        return commands.insertContent({
                            type: this.name,
                            attrs: {
                                deviation: toLightweightDeviation(deviation)
                            }
                        });
                    }
                    return true;
                },
            daDeviationOpenSettingsMenu: ()=>({ editor, chain, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 1;
                    }
                    return true;
                },
            daDeviationCloseSettingsMenu: ()=>({ editor, chain, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 0;
                    }
                    return true;
                },
            daDeviationAlign: (alignment)=>({ editor, chain, dispatch, commands })=>{
                    if (!this.options.canAlign) {
                        return false;
                    }
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            alignment
                        });
                    }
                    return true;
                },
            daDeviationLink: (href)=>({ editor, chain, dispatch, commands })=>{
                    if (!this.options.canLink) {
                        return false;
                    }
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            link: href
                        });
                    }
                    return true;
                },
            daDeviationDelete: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (deleteExtension(EXTENSION_TYPE_NAME, editor, dispatch, state)) {
                        this.storage.menuState = 0;
                        return true;
                    }
                    return true;
                },
            daDeviationRefresh: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        const { selection } = state;
                        const { from, to } = selection;
                        let attributes = null;
                        state.doc.nodesBetween(from, to, (node)=>{
                            if (node.type.name === this.name) {
                                attributes = node.attrs;
                            }
                        });
                        if (attributes) {
                            fetchFreshDeviations({
                                deviations: [
                                    attributes.deviation
                                ],
                                editor
                            }).then((result)=>{
                                if (result.error) {
                                    alert(result.error);
                                } else {
                                    editor.chain().focus().daDeviationReplace(result[0]).run();
                                }
                            }).catch((e)=>{
                                alert(e.message);
                            });
                        }
                        return true;
                    }
                    return true;
                },
            daDeviationReplace: (deviation)=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        commands.updateAttributes(this.name, {
                            deviation: toLightweightDeviation(deviation)
                        });
                        return true;
                    }
                    return true;
                },
            daDeviationConvertToThumb: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        const { selection } = state;
                        const { from, to } = selection;
                        let attributes = null;
                        state.doc.nodesBetween(from, to, (node)=>{
                            if (node.type.name === this.name) {
                                attributes = node.attrs;
                            }
                        });
                        if (attributes) {
                            var _attributes_link;
                            return chain().daDeviationDelete().daDeviationThumbAddDeviation(attributes.deviation).daDeviationThumbLink((_attributes_link = attributes.link) !== null && _attributes_link !== void 0 ? _attributes_link : '').run();
                        }
                        return true;
                    }
                    return true;
                },
            daDeviationToLink: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        const { selection } = state;
                        const { from, to } = selection;
                        let attributes = null;
                        state.doc.nodesBetween(from, to, (node)=>{
                            if (node.type.name === this.name) {
                                attributes = node.attrs;
                            }
                        });
                        if (attributes && attributes.deviation) {
                            const deviation = attributes.deviation;
                            let link = '';
                            if (deviation.isPublished) {
                                var _deviation_url, _ref;
                                link = (_ref = (_deviation_url = deviation.url) !== null && _deviation_url !== void 0 ? _deviation_url : deviation.shortUrl) !== null && _ref !== void 0 ? _ref : '';
                            } else {
                                var _deviation_stashPrivateid;
                                var _deviation_stashPrivateid_toString;
                                link = Url.stashItemLink((_deviation_stashPrivateid_toString = (_deviation_stashPrivateid = deviation.stashPrivateid) === null || _deviation_stashPrivateid === void 0 ? void 0 : _deviation_stashPrivateid.toString(36)) !== null && _deviation_stashPrivateid_toString !== void 0 ? _deviation_stashPrivateid_toString : '');
                            }
                            var _deviation_title;
                            chain().daDeviationDelete().insertContent({
                                type: 'text',
                                marks: [
                                    {
                                        type: 'link',
                                        attrs: {
                                            href: link
                                        }
                                    }
                                ],
                                text: (_deviation_title = deviation.title) !== null && _deviation_title !== void 0 ? _deviation_title : link
                            }).run();
                        }
                        return true;
                    }
                    return true;
                }
        };
    },
    addProseMirrorPlugins () {
        return [
            new Plugin({
                key: new PluginKey('da-deviation-paste'),
                props: {
                    handlePaste: (view, event, slice)=>{
                        var _event_clipboardData;
                        var _event_clipboardData_getData;
                        const text = (_event_clipboardData_getData = (_event_clipboardData = event.clipboardData) === null || _event_clipboardData === void 0 ? void 0 : _event_clipboardData.getData('text/plain')) !== null && _event_clipboardData_getData !== void 0 ? _event_clipboardData_getData : '';
                        if (!/^(https?:\/\/|:thumb)/.test(text)) {
                            return false;
                        }
                        const matchedDeviation = elmoSaysThisMightBeADeviationUrl(text);
                        if (matchedDeviation) {
                            this.editor.setEditable(false);
                            fetchDeviation(this.editor, matchedDeviation).then((result)=>{
                                this.editor.setEditable(true);
                                if (result.error) {
                                    this.editor.chain().insertContent({
                                        type: 'text',
                                        marks: [
                                            {
                                                type: 'link',
                                                attrs: {
                                                    href: text
                                                }
                                            }
                                        ],
                                        text: text
                                    }).run();
                                } else {
                                    this.editor.chain().focus().daDeviationAddDeviation(result).run();
                                }
                            }).catch((e)=>{
                                this.editor.setEditable(true);
                                this.editor.chain().insertContent({
                                    type: 'text',
                                    marks: [
                                        {
                                            type: 'link',
                                            attrs: {
                                                href: text
                                            }
                                        }
                                    ],
                                    text: text
                                }).run();
                            });
                            return true;
                        }
                        return false;
                    }
                }
            })
        ];
    }
});

//# sourceMappingURL=deviation.js.map